import {
  Children,
  type ReactElement,
  type ReactNode,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
} from 'react';

type HelmetProviderProps = {
  children: ReactNode;
};

type HelmetProps = {
  children?: ReactNode;
};

type MetaAttributes = Record<string, string>;
type HelmetElement = ReactElement<Record<string, unknown>, string>;

type HelmetState = {
  id: number;
  title?: string;
  metas: MetaAttributes[];
};

const MANAGED_META_ATTR = 'data-tagna-helmet';
const hasDOM = typeof document !== 'undefined';
const initialTitle = hasDOM ? document.title : '';

const helmetStates: HelmetState[] = [];
let nextHelmetId = 1;

function getTopHelmetState(): HelmetState | undefined {
  return helmetStates[helmetStates.length - 1];
}

function getMetaKey(meta: MetaAttributes, fallback: string): string {
  if (meta.name) return `name:${meta.name}`;
  if (meta.property) return `property:${meta.property}`;
  if (meta['http-equiv']) return `http-equiv:${meta['http-equiv']}`;
  if (meta.charset) return `charset:${meta.charset}`;
  if (meta.itemProp) return `itemProp:${meta.itemProp}`;
  return fallback;
}

function getMergedHelmetState(): Omit<HelmetState, 'id'> {
  let title: string | undefined;
  const metaByKey = new Map<string, MetaAttributes>();

  for (const state of helmetStates) {
    if (state.title !== undefined) {
      title = state.title;
    }

    for (let i = 0; i < state.metas.length; i += 1) {
      const meta = state.metas[i];
      const key = getMetaKey(meta, `state:${state.id}:meta:${i}`);
      metaByKey.delete(key);
      metaByKey.set(key, meta);
    }
  }

  return {
    title,
    metas: Array.from(metaByKey.values()),
  };
}

function isHelmetElement(node: ReactNode): node is HelmetElement {
  return isValidElement(node) && typeof node.type === 'string';
}

function readTextContent(node: HelmetElement): string {
  return Children.toArray(node.props.children as ReactNode)
    .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
    .join('');
}

function normalizeMetaAttributes(node: HelmetElement): MetaAttributes {
  const attributes: MetaAttributes = {};

  for (const [key, value] of Object.entries(node.props)) {
    if (key === 'children' || value === undefined || value === null) {
      continue;
    }

    if (
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      typeof value !== 'boolean'
    ) {
      continue;
    }

    const attrKey =
      key === 'charSet' ? 'charset' : key === 'httpEquiv' ? 'http-equiv' : key;

    attributes[attrKey] = String(value);
  }

  return attributes;
}

function parseHelmetChildren(children: ReactNode): Omit<HelmetState, 'id'> {
  const parsed: Omit<HelmetState, 'id'> = { title: undefined, metas: [] };

  Children.forEach(children, (child) => {
    if (!isHelmetElement(child)) {
      return;
    }

    const tagName = child.type.toLowerCase();

    if (tagName === 'title') {
      parsed.title = readTextContent(child);
      return;
    }

    if (tagName === 'meta') {
      parsed.metas.push(normalizeMetaAttributes(child));
    }
  });

  return parsed;
}

function syncDocumentHead() {
  if (!hasDOM) {
    return;
  }

  if (!getTopHelmetState()) {
    document.title = initialTitle;
  } else {
    const merged = getMergedHelmetState();
    document.title = merged.title ?? initialTitle;

    document.head
      .querySelectorAll(`meta[${MANAGED_META_ATTR}="true"]`)
      .forEach((element) => element.remove());

    for (const meta of merged.metas) {
      const element = document.createElement('meta');
      element.setAttribute(MANAGED_META_ATTR, 'true');

      for (const [key, value] of Object.entries(meta)) {
        element.setAttribute(key, value);
      }

      document.head.appendChild(element);
    }
  }
}

export function HelmetProvider({ children }: HelmetProviderProps) {
  return <>{children}</>;
}

export function Helmet({ children }: HelmetProps) {
  const helmetIdRef = useRef<number | null>(null);

  if (helmetIdRef.current === null) {
    helmetIdRef.current = nextHelmetId++;
  }

  const parsed = useMemo(() => parseHelmetChildren(children), [children]);

  useEffect(() => {
    const id = helmetIdRef.current!;
    const index = helmetStates.findIndex((state) => state.id === id);
    const nextState: HelmetState = { id, ...parsed };

    if (index >= 0) {
      helmetStates[index] = nextState;
    } else {
      helmetStates.push(nextState);
    }

    syncDocumentHead();

    return () => {
      const existingIndex = helmetStates.findIndex((state) => state.id === id);
      if (existingIndex >= 0) {
        helmetStates.splice(existingIndex, 1);
      }
      syncDocumentHead();
    };
  }, [parsed]);

  return null;
}
